import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { pickBy, isNil } from 'lodash';

type Backup<T> = {
  value: T;
  expired: number;
};

type LocalStorageState<T> = {
  backups: Record<string, Backup<T>>;
  setBackup: (key: string, value: T, expired: number) => void;
  getBackup: (key: string) => Backup<T> | undefined;
  clearBackup: (key: string) => void;
  bulkClearBackupsForAssignment: (assignmentId: string, responseIndex?: number) => void;
};

export const CHAT_LOCAL_STORAGE_BACKUPS_KEY = 'chat/backups';

// Create a dummy store for SSR
const createNoopStore = () =>
  create<LocalStorageState<any>>()(() => ({
    backups: {},
    setBackup: () => {},
    getBackup: () => undefined,
    clearBackup: () => {},
    bulkClearBackupsForAssignment: () => {},
  }));

// Create the actual store with persistence
const createStore = () =>
  create<LocalStorageState<any>>()(
    devtools(
      persist(
        (set, get) => ({
          backups: {},
          setBackup: (key, value, expired) => {
            const now = Date.now();
            try {
              set(state => {
                const nonExpiredBackups = pickBy(state.backups, backup => backup.expired >= now);
                const newBackups = { ...nonExpiredBackups, [key]: { value, expired } };
                return { backups: newBackups };
              });
            } catch (e) {
              localStorage.removeItem(CHAT_LOCAL_STORAGE_BACKUPS_KEY);
            }
          },
          getBackup: key => {
            try {
              return get().backups[key];
            } catch (e) {
              localStorage.removeItem(CHAT_LOCAL_STORAGE_BACKUPS_KEY);
              return undefined;
            }
          },
          clearBackup: key => {
            try {
              set(state => {
                const { [key]: _, ...otherBackups } = state.backups;
                return { backups: otherBackups };
              });
            } catch (e) {
              localStorage.removeItem(CHAT_LOCAL_STORAGE_BACKUPS_KEY);
            }
          },
          bulkClearBackupsForAssignment: (assignmentId, responseIndex) => {
            try {
              set(state => {
                const otherBackups = pickBy(state.backups, (_, key) => {
                  const splitKey = key.split('-');
                  if (splitKey.length !== 3) {
                    return true;
                  }
                  if (splitKey[0] !== assignmentId) {
                    return true;
                  }
                  const backupResponseIndex = parseInt(splitKey[2]);
                  if (
                    !isNil(responseIndex) &&
                    isFinite(backupResponseIndex) &&
                    backupResponseIndex <= responseIndex
                  ) {
                    return true;
                  }
                  return false;
                });
                return { backups: otherBackups };
              });
            } catch (e) {
              localStorage.removeItem(CHAT_LOCAL_STORAGE_BACKUPS_KEY);
            }
          },
        }),
        {
          name: CHAT_LOCAL_STORAGE_BACKUPS_KEY,
          getStorage: () => localStorage,
        },
      ),
    ),
  );

// Export the appropriate store based on environment
export const useLocalStorageBackupStore =
  typeof window !== 'undefined' ? createStore() : createNoopStore();
